import { createContext, useContext, useState, useEffect, type ReactNode, type FC } from 'react';
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';

/**
 * Cognito認証の設定
 */
const getCognitoConfig = () => {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '';

  if (!userPoolId || !clientId) {
    // Cognito設定が環境変数に設定されていません
  }

  return {
    userPoolId,
    clientId,
  };
};

const poolData = getCognitoConfig();
const userPool = poolData.userPoolId && poolData.clientId
  ? new CognitoUserPool({
      UserPoolId: poolData.userPoolId,
      ClientId: poolData.clientId,
    })
  : null;

/**
 * 認証状態の型定義
 */
interface AuthContextType {
  user: CognitoUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresNewPassword: boolean;
  pendingUsername: string | null;
  login: (username: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => void;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * 認証プロバイダーコンポーネント
 */
export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [requiresNewPassword, setRequiresNewPassword] = useState<boolean>(false);
  const [pendingUsername, setPendingUsername] = useState<string | null>(null);
  const [pendingCognitoUser, setPendingCognitoUser] = useState<CognitoUser | null>(null);

  /**
   * 現在のセッションを確認
   */
  useEffect(() => {
    if (!userPool) {
      setIsLoading(false);
      return;
    }

    const checkSession = async () => {
      try {
        const currentUser = userPool.getCurrentUser();
        if (currentUser) {
          currentUser.getSession((err: Error | null, session: any) => {
            if (err || !session || !session.isValid()) {
              // セッションが無効な場合は、ユーザーをサインアウト
              currentUser.signOut();
              setUser(null);
              setIsAuthenticated(false);
              setIsLoading(false);
            } else {
              setUser(currentUser);
              setIsAuthenticated(true);
              setIsLoading(false);
            }
          });
        } else {
          // ユーザーが存在しない場合は、認証状態をクリア
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  /**
   * ログイン処理
   * USER_PASSWORD_AUTHフローを使用
   */
  const login = async (username: string, password: string): Promise<void> => {
    if (!userPool) {
      throw new Error('Cognito User Poolが設定されていません');
    }

    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      // USER_PASSWORD_AUTHフローを設定
      cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

      // AuthenticationDetailsを作成
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      // authenticateUserを呼び出し
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: () => {
          // セッションを明示的に取得してから状態を更新
          cognitoUser.getSession((err: Error | null, session: any) => {
            if (err || !session || !session.isValid()) {
              setRequiresNewPassword(false);
              setPendingUsername(null);
              setPendingCognitoUser(null);
              reject(err || new Error('セッションの取得に失敗しました'));
              return;
            }
            setUser(cognitoUser);
            setIsAuthenticated(true);
            setRequiresNewPassword(false);
            setPendingUsername(null);
            setPendingCognitoUser(null);
            resolve();
          });
        },
        onFailure: (err: Error) => {
          setRequiresNewPassword(false);
          setPendingUsername(null);
          setPendingCognitoUser(null);
          reject(err);
        },
        newPasswordRequired: (_userAttributes: any, _requiredAttributes: any) => {
          // 初回ログイン時のパスワード変更が必要な場合
          setPendingCognitoUser(cognitoUser);
          setPendingUsername(username);
          setRequiresNewPassword(true);
          resolve();
        },
      });
    });
  };

  /**
   * 新しいパスワードを設定
   */
  const completeNewPassword = async (newPassword: string): Promise<void> => {
    if (!pendingCognitoUser) {
      throw new Error('パスワード変更が必要なセッションが見つかりません');
    }

    return new Promise((resolve, reject) => {
      pendingCognitoUser.completeNewPasswordChallenge(
        newPassword,
        {}, // requiredAttributes（必要に応じて設定）
        {
          onSuccess: () => {
            setUser(pendingCognitoUser);
            setIsAuthenticated(true);
            setRequiresNewPassword(false);
            setPendingUsername(null);
            setPendingCognitoUser(null);
            resolve();
          },
          onFailure: (err: Error) => {
            reject(err);
          },
        }
      );
    });
  };

  /**
   * ログアウト処理
   */
  const logout = () => {
    if (user) {
      user.signOut();
    }
    setUser(null);
    setIsAuthenticated(false);
    setRequiresNewPassword(false);
    setPendingUsername(null);
    setPendingCognitoUser(null);
  };

  /**
   * IDトークンを取得
   */
  const getIdToken = async (): Promise<string | null> => {
    if (!user) {
      return null;
    }

    return new Promise((resolve, reject) => {
      user.getSession((err: Error | null, session: any) => {
        if (err || !session) {
          reject(err || new Error('セッションが無効です'));
          return;
        }

        const idToken = session.getIdToken().getJwtToken();
        resolve(idToken);
      });
    });
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    requiresNewPassword,
    pendingUsername,
    login,
    completeNewPassword,
    logout,
    getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * 認証コンテキストを使用するカスタムフック
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * 現在のIDトークンを取得するヘルパー関数
 * この関数はReactコンポーネント外でも使用可能
 */
export const getCurrentIdToken = async (): Promise<string | null> => {
  if (!userPool) {
    return null;
  }

  const currentUser = userPool.getCurrentUser();
  if (!currentUser) {
    return null;
  }

  return new Promise((resolve) => {
    currentUser.getSession((err: Error | null, session: any) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }

      const idToken = session.getIdToken().getJwtToken();
      resolve(idToken);
    });
  });
};

/**
 * 現在の認証状態をチェックするヘルパー関数
 * この関数はReactコンポーネント外でも使用可能
 * 
 * @returns 認証済みの場合true、それ以外false
 */
export const isUserAuthenticated = async (): Promise<boolean> => {
  if (!userPool) {
    return false;
  }

  const currentUser = userPool.getCurrentUser();
  if (!currentUser) {
    return false;
  }

  return new Promise((resolve) => {
    currentUser.getSession((err: Error | null, session: any) => {
      if (err || !session || !session.isValid()) {
        resolve(false);
        return;
      }
      resolve(true);
    });
  });
};
